import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRollbar } from '@rollbar/react';
import isEqual from 'lodash/isEqual';

import { setProject } from '../obj/pscState';
import useAuthentication from '../hooks/useAuthentication';

const ProjectContentsContext = createContext();

export const ProjectContentsProvider = ({ children }) => {
    const rollbar = useRollbar();
    const { currentUser } = useAuthentication();
    
    const dispatch = useDispatch();
    const pscState = useSelector(state => state.pscState.value);
    const project = pscState.project;
    const config = pscState.config || {};
    
    const cacheSeconds = 30;
    const [projectID, setProjectID] = useState(0);
    const [lastUpdated, setLastUpdated] = useState(0);
    
    const [customerDocuments, _setCustomerDocuments] = useState([]);
    const [includedDocuments, _setIncludedDocuments] = useState([]);
    const [documents, _setDocuments] = useState([]);
    const [records, _setRecords] = useState([]);
    const [deliveryLists, _setDeliveryLists] = useState([]);
    const [reports, _setReports] = useState([]);
    const [actionItemSources, _setActionItemSources] = useState([]);
    const [actionItems, _setActionItems] = useState([]);

    // Individual loaded states for each content type (start false, become true once loaded)
    const [isCustomerDocumentsLoaded, setIsCustomerDocumentsLoaded] = useState(false);
    const [isIncludedDocumentsLoaded, setIsIncludedDocumentsLoaded] = useState(false);
    const [isDocumentsLoaded, setIsDocumentsLoaded] = useState(false);
    const [isRecordsLoaded, setIsRecordsLoaded] = useState(false);
    const [isDeliveryListsLoaded, setIsDeliveryListsLoaded] = useState(false);
    const [isReportsLoaded, setIsReportsLoaded] = useState(false);
    const [isActionItemSourcesLoaded, setIsActionItemSourcesLoaded] = useState(false);
    const [isActionItemsLoaded, setIsActionItemsLoaded] = useState(false);
    
    // Track ongoing reload to prevent redundant calls
    const reloadInProgressRef = useRef(false);
    const currentReloadKeyRef = useRef(null);
    const lastReloadCompletedRef = useRef(0);
    const abortControllerRef = useRef(null);
    const lastReloadKeyRef = useRef(null);
    const lastReloadUserHashRef = useRef(null);
    const currentProjectIdRef = useRef(project?.id);
    currentProjectIdRef.current = project?.id;

    // Helper to create a simple hash of an object
    const hashObject = useCallback((obj) => {
        if (!obj) return '0';
        const str = JSON.stringify(obj);
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32bit integer
        }
        return hash.toString();
    }, []);

    // Clean up old localStorage keys (migration from old implementation)
    useEffect(() => {
        localStorage.removeItem('project_contents_project_id');
        localStorage.removeItem('project_contents_last_updated');
        localStorage.removeItem('project_contents_customer_documents');
        localStorage.removeItem('project_contents_included_documents');
        localStorage.removeItem('project_contents_documents');
        localStorage.removeItem('project_contents_records');
        localStorage.removeItem('project_contents_delivery_lists');
        localStorage.removeItem('project_contents_reports');
        localStorage.removeItem('project_action_item_sources');
        localStorage.removeItem('project_action_items');
    }, []);

    // The _process methods slim down the data we keep in state
    const __process = useCallback((row) => {
        const item = {};
        item.id = row.id;
        item.reference = row.reference;
        item.title = row.title;
        item.owner_user_id = row.owner_user_id;
        item.created_at = row.created_at;
        item.final_version_file_path = row.final_version_file_path;
        item.file_paths = row.file_paths;
        item.project_id = row.project_id;
        item.std_document = { name: row.std_document?.name, id: row.std_document?.id };
        return item;
    }, []);
    
    const _processCustomerDocument = useCallback((row) => {
        const item = __process(row);
        item.customer_document_status = { name: row.customer_document_status.name, id: row.customer_document_status.id };
        item.organisation_code = row.organisation_code;
        return item;
    }, [__process]);

    const _processIncludedDocument = useCallback((row) => {
        const item = __process(row);
        item.original = row.original;
        return item;
    }, [__process]);
    
    const _processDocument = useCallback((row) => {
        const item = __process(row);
        item.document_status = { name: row.document_status.name, id: row.document_status.id };
        return item;
    }, [__process]);

    const _processRecord = useCallback((row) => {
        const item = __process(row);
        item.record_status = { name: row.record_status.name, id: row.record_status.id };
        return item;
    }, [__process]);

    const _processDeliveryList = useCallback((row) => {
        const item = __process(row);
        item.sent_date = row.sent_date;
        item.delivered_by_user_id = row.delivered_by_user_id;
        item.delivered_to = row.delivered_to;
        item.delivery_list_status = row.delivery_list_status;
        return item;
    }, [__process]);
    
    const _processReport = useCallback((row) => {
        const item = __process(row);
        item.create_date = row.create_date;
        item.report_scheme = row.report_scheme;
        item.report_status = row.report_status;
        return item;
    }, [__process]);
    
    const _processActionItemSource = useCallback((row) => {
        const item = __process(row);
        item.name = row.name;
        item.source_date = row.source_date;
        item.default_item_type = row.default_item_type;
        item.location = row.location;
        item.action_count_total = row.action_count_total;
        item.action_count_open = row.action_count_open;
        item.action_count_closed = row.action_count_closed;
        return item;
    }, [__process]);
    
    const _processActionItem = useCallback((row) => {
        const item = __process(row);
        item.action_item_source = row.action_item_source;
        item.action_item_source_id = row.action_item_source_id;
        item.type = row.type;
        item.raised_date = row.raised_date;
        item.action_item_number = row.action_item_number;
        item.origin = row.origin;
        item.description = row.description;
        item.owner_freetext = row.owner_freetext;
        item.due_date = row.due_date;
        item.due_date_freetext = row.due_date_freetext;
        item.status = row.status;
        item.close_reference = row.close_reference;
        item.comments = row.comments;
        return item;
    }, [__process]);
    
    const _reload = useCallback((objType, list, setCb, processCb, setLoadedCb, signal) => {
        if (!project || !project.id) {
            return Promise.resolve();
        }
        const requestedProjectId = project.id;
        return fetch('/v0/' + objType + '?project_id=' + project.id, { credentials: 'same-origin', signal })
            .then(function(response) {
                if (!response.ok) throw response;
                return response.json();
            })
            .then(function(json) {
                if (signal && signal.aborted) return;
                if (currentProjectIdRef.current !== requestedProjectId) return;
                if (json.error && json.error === 'ENOACCESS') {
                    setCb([]);
                    if (setLoadedCb) setLoadedCb(true);
                    return;
                }
                const data = json.results.map((row) => {
                    return processCb(row);
                });
                if (!isEqual(data, list)) {
                    setCb(data);
                }
                if (setLoadedCb) setLoadedCb(true);
            })
            .catch(error => {
                if (error.name === 'AbortError') return;
                console.log("Reload error", objType, error);
                if (signal && signal.aborted) return;
                if (currentProjectIdRef.current !== requestedProjectId) return;
                if (setLoadedCb) setLoadedCb(true); // Mark as loaded even on error to avoid infinite loading
            });
    }, [project]);
    
    const _reloadCustomerDocuments = useCallback((signal) => _reload('customer_documents', customerDocuments, _setCustomerDocuments, _processCustomerDocument, setIsCustomerDocumentsLoaded, signal), [_reload, customerDocuments, _setCustomerDocuments, _processCustomerDocument]);
    const _reloadIncludedDocuments = useCallback((signal) => _reload('included_documents', includedDocuments, _setIncludedDocuments, _processIncludedDocument, setIsIncludedDocumentsLoaded, signal), [_reload, includedDocuments, _setIncludedDocuments, _processIncludedDocument]);
    const _reloadDocuments = useCallback((signal) => _reload('documents', documents, _setDocuments, _processDocument, setIsDocumentsLoaded, signal), [_reload, documents, _setDocuments, _processDocument]);
    const _reloadRecords = useCallback((signal) => _reload('records', records, _setRecords, _processRecord, setIsRecordsLoaded, signal), [_reload, records, _setRecords, _processRecord]);
    const _reloadDeliveryLists = useCallback((signal) => _reload('delivery_lists', deliveryLists, _setDeliveryLists, _processDeliveryList, setIsDeliveryListsLoaded, signal), [_reload, deliveryLists, _setDeliveryLists, _processDeliveryList]);
    const _reloadReports = useCallback((signal) => _reload('reports', reports, _setReports, _processReport, setIsReportsLoaded, signal), [_reload, reports, _setReports, _processReport]);
    const _reloadActionItemSources = useCallback((signal) => _reload('action_item_sources', actionItemSources, _setActionItemSources, _processActionItemSource, setIsActionItemSourcesLoaded, signal), [_reload, actionItemSources, _setActionItemSources, _processActionItemSource]);
    const _reloadActionItems = useCallback((signal) => _reload('action_items', actionItems, _setActionItems, _processActionItem, setIsActionItemsLoaded, signal), [_reload, actionItems, _setActionItems, _processActionItem]);

    const reloadProjectContents = useCallback(() => {
        if (typeof(project) === 'undefined' || !project.id) {
            console.log("ProjectContents: trying to reload all, but project is not initialized yet");
            return;
        }
        
        // Create a unique key for this reload based on project ID and hash of currentUser object
        // This ensures any change to user (roles, permissions, etc.) triggers a reload
        const userHash = hashObject(currentUser);
        const reloadKey = `${project.id}_${userHash}`;
        
        // If already reloading with the same key, skip this call
        if (reloadInProgressRef.current && currentReloadKeyRef.current === reloadKey) {
            console.log("ProjectContents: Reload already in progress for project", project.id, "- skipping");
            return;
        }
        
        // If the last reload completed less than 1 second ago for the SAME project/user, skip this call
        const timeSinceLastReload = Date.now() - lastReloadCompletedRef.current;
        if (timeSinceLastReload < 1000 && lastReloadKeyRef.current === reloadKey) {
            console.log("ProjectContents: Last reload completed", timeSinceLastReload, "ms ago for same project - skipping (too soon)");
            return;
        }
        
        // If reloading with a different key, the new reload will supersede the old one
        if (reloadInProgressRef.current && currentReloadKeyRef.current !== reloadKey) {
            console.log("ProjectContents: Project or user changed - superseding previous reload");
        }
        
        // Mark reload as in progress with this key
        reloadInProgressRef.current = true;
        currentReloadKeyRef.current = reloadKey;
        
        // Abort any in-flight fetches from a previous reload so their results cannot overwrite this one
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;
        
        // Reset all loaded states
        setIsCustomerDocumentsLoaded(false);
        setIsIncludedDocumentsLoaded(false);
        setIsDocumentsLoaded(false);
        setIsRecordsLoaded(false);
        setIsDeliveryListsLoaded(false);
        setIsReportsLoaded(false);
        setIsActionItemSourcesLoaded(false);
        setIsActionItemsLoaded(false);
        
        const timestamp = Date.now();
        
        // Update state
        setLastUpdated(timestamp);
        setProjectID(project.id);
        
        // Now fetch the data
        // Wait for all fetches to complete before marking as done
        Promise.all([
            _reloadCustomerDocuments(controller.signal),
            _reloadIncludedDocuments(controller.signal),
            _reloadDocuments(controller.signal),
            _reloadRecords(controller.signal),
            _reloadDeliveryLists(controller.signal),
            _reloadReports(controller.signal),
            _reloadActionItemSources(controller.signal),
            _reloadActionItems(controller.signal)
        ]).finally(() => {
            if (abortControllerRef.current === controller) {
                reloadInProgressRef.current = false;
                lastReloadCompletedRef.current = Date.now();
                lastReloadKeyRef.current = reloadKey;
            }
        });
    }, [project, currentUser, hashObject, _reloadCustomerDocuments, _reloadIncludedDocuments, _reloadDocuments, _reloadRecords, _reloadDeliveryLists, _reloadReports, _reloadActionItemSources, _reloadActionItems]);
    
    const clearProjectContents = useCallback(async () => {
        setProjectID(0);
        _setCustomerDocuments([]);
        _setIncludedDocuments([]);
        _setDocuments([]);
        _setRecords([]);
        _setDeliveryLists([]);
        _setReports([]);
        _setActionItemSources([]);
        _setActionItems([]);
        setLastUpdated(0);
    }, []);

    // Auto-reload when project or current user changes
    useEffect(() => {
        if (project === null || typeof(project) === 'undefined' || typeof(project.id) === 'undefined' || typeof(projectID) === 'undefined') {
            clearProjectContents();
            return;
        }

        const userHash = hashObject(currentUser);

        // Always reload if the project has changed
        if (project.id !== projectID) {
            // Only clear and reload if we actually had a different project loaded
            // If projectID is 0, we're just initializing, so don't clear
            if (projectID !== 0 && projectID !== project.id) {
                console.log("ProjectContents: Project ID changed from", projectID, "to", project.id, "- clearing old data and reloading");
                // Clear all data immediately before the new reload repopulates it
                _setCustomerDocuments([]);
                _setIncludedDocuments([]);
                _setDocuments([]);
                _setRecords([]);
                _setDeliveryLists([]);
                _setReports([]);
                _setActionItemSources([]);
                _setActionItems([]);
            } else if (projectID === 0) {
                console.log("ProjectContents: Initial project load for project", project.id);
            }
            // Now reload with new project data (this will update projectID)
            lastReloadUserHashRef.current = userHash;
            reloadProjectContents();
            return;
        }

        // Reload immediately when the current user changes (roles/permissions may have changed)
        if (lastReloadUserHashRef.current !== userHash) {
            lastReloadUserHashRef.current = userHash;
            reloadProjectContents();
            return;
        }

        // Check if we need to refresh based on cache timeout
        if (lastUpdated + (1000 * cacheSeconds) > Date.now()) {
            return;
        }
        reloadProjectContents();
    }, [project, projectID, lastUpdated, cacheSeconds, currentUser, clearProjectContents, reloadProjectContents, hashObject]);

    // Abort any pending fetches when the provider unmounts
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);
    
    const _updateOrInsert = useCallback((list, data) => {
        const index = list.findIndex(item => item.id === data.id);
        if (index >= 0) {
            list[index] = data;
        } else {
            list.push(data);
        }
        return list;
    }, []);

    const _update = useCallback((input, process, list, setCb) => {
        const data = process(input);
        const updated = _updateOrInsert(list, data);
        setCb(updated);
    }, [_updateOrInsert]);

    const _delete = useCallback((id, list, setCb) => {
        const updated = list.filter(item => item.id !== id);
        setCb(updated);
    }, []);
    
    const updateCustomerDocument = useCallback((item) => _update(item, _processCustomerDocument, customerDocuments, _setCustomerDocuments), [_update, _processCustomerDocument, customerDocuments, _setCustomerDocuments]);
    const deleteCustomerDocument = useCallback((id) => _delete(id, customerDocuments, _setCustomerDocuments), [_delete, customerDocuments, _setCustomerDocuments]);
    
    const updateIncludedDocument = useCallback((item) => _update(item, _processIncludedDocument, includedDocuments, _setIncludedDocuments), [_update, _processIncludedDocument, includedDocuments, _setIncludedDocuments]);
    const deleteIncludedDocument = useCallback((id) => _delete(id, includedDocuments, _setIncludedDocuments), [_delete, includedDocuments, _setIncludedDocuments]);

    const updateDocument = useCallback((item) => _update(item, _processDocument, documents, _setDocuments), [_update, _processDocument, documents, _setDocuments]);
    const deleteDocument = useCallback((id) => _delete(id, documents, _setDocuments), [_delete, documents, _setDocuments]);
    
    const updateRecord = useCallback((item) => _update(item, _processRecord, records, _setRecords), [_update, _processRecord, records, _setRecords]);
    const deleteRecord = useCallback((id) => _delete(id, records, _setRecords), [_delete, records, _setRecords]);
        
    const updateDeliveryList = useCallback((item) => _update(item, _processDeliveryList, deliveryLists, _setDeliveryLists), [_update, _processDeliveryList, deliveryLists, _setDeliveryLists]);
    const deleteDeliveryList = useCallback((id) => _delete(id, deliveryLists, _setDeliveryLists), [_delete, deliveryLists, _setDeliveryLists]);
    
    const updateReport = useCallback((item) => _update(item, _processReport, reports, _setReports), [_update, _processReport, reports, _setReports]);
    const deleteReport = useCallback((id) => _delete(id, reports, _setReports), [_delete, reports, _setReports]);
    
    const updateActionItemSource = useCallback((item) => _update(item, _processActionItemSource, actionItemSources, _setActionItemSources), [_update, _processActionItemSource, actionItemSources, _setActionItemSources]);
    const deleteActionItemSource = useCallback((id) => _delete(id, actionItemSources, _setActionItemSources), [_delete, actionItemSources, _setActionItemSources]);

    // Sources have stats on action items, which is a PITA to sort out client-side.. so just go reload the sources #lazy #ftw
    const updateActionItem = useCallback((item) => { 
        _update(item, _processActionItem, actionItems, _setActionItems); 
        _reloadActionItemSources();
    }, [_update, _processActionItem, actionItems, _setActionItems, _reloadActionItemSources]);
    
    const deleteActionItem = useCallback((id) => { 
        _delete(id, actionItems, _setActionItems); 
        _reloadActionItemSources();
    }, [_delete, actionItems, _setActionItems, _reloadActionItemSources]);

    // When doing actions that might affect other items (for example changing Status on a Doc can change the Statuses of several other docs)
    // we do need a full reload of the contents
    const forceReloadDocuments = useCallback(async () => {
        await _reloadDocuments();
    }, [_reloadDocuments]);
    
    const forceReloadCustomerDocuments = useCallback(async () => {
        await _reloadCustomerDocuments();
    }, [_reloadCustomerDocuments]);
    
    const value = {
        // Individual loaded states for each content type
        isCustomerDocumentsLoaded,
        isIncludedDocumentsLoaded,
        isDocumentsLoaded,
        isRecordsLoaded,
        isDeliveryListsLoaded,
        isReportsLoaded,
        isActionItemSourcesLoaded,
        isActionItemsLoaded,
        
        reloadProjectContents,
        clearProjectContents,

        // Access project contents
        customerDocuments,
        includedDocuments,
        documents,
        records,
        deliveryLists,
        reports,
        actionItemSources,
        actionItems,

        //
        forceReloadDocuments,
        forceReloadCustomerDocuments,
        
        // Update / delete individual items
        updateCustomerDocument,
        deleteCustomerDocument,
        updateIncludedDocument,
        deleteIncludedDocument,
        updateDocument,
        deleteDocument,
        updateRecord,
        deleteRecord,
        updateDeliveryList,
        deleteDeliveryList,
        updateReport,
        deleteReport,
        updateActionItemSource,
        deleteActionItemSource,
        updateActionItem,
        deleteActionItem,
    };

    return (
        <ProjectContentsContext.Provider value={value}>
            {children}
        </ProjectContentsContext.Provider>
    );
};

export const useProjectContentsContext = () => {
    const context = useContext(ProjectContentsContext);
    if (!context) {
        throw new Error('useProjectContentsContext must be used within a ProjectContentsProvider');
    }
    return context;
};
